import _ from 'lodash'

import InfiniteLoading from 'vue-infinite-loading'

import { mapActions, mapGetters } from 'vuex'

import Vue from 'vue'

import { api } from '_/services/api'

import { config } from '_/config/config'

import { pError } from '_/helpers/logger'

import { store } from '_/vuex/store'

import { getObjectPropertyValueByPath } from '_/helpers/lodashHelper'

const Layout = Vue.component(
  'Layout',
  require('_/components/FieldTypes/AssetsManager/Layout/Layout.vue').default
)

const ImageSizes = Vue.component(
  'ImageSizes',
  require('_/components/FieldTypes/Select2/Select2.vue').default
)

const FocalPointPicker = Vue.component(
  'FocalPointPicker',
  require('_/components/FieldTypes/AssetsManager/FocalPointPicker/FocalPointPicker.vue')
    .default
)

const AssetEditor = Vue.component(
  'AssetEditor',
  require('_/components/FieldTypes/AssetsManager/AssetEditor/AssetEditor.vue')
    .default
)

const FileUploadForm = Vue.component(
  'FileUploadForm',
  require('_/components/FieldTypes/AssetsManager/FileUploadForm/FileUploadForm.vue')
    .default
)

const AssetAdvancedSearch = Vue.component(
  'AssetAdvancedSearch',
  require('_/components/FieldTypes/AssetsManager/AssetAdvancedSearch/AssetAdvancedSearch.vue')
    .default
)

export default {
  /**
   * Define props
   *
   * @type  {Object}
   */
  props: {
    registeredModuleName: {
      type: String,
      required: true,
    },
  },

  /**
   * Set the data property
   *
   * @return  {object}
   */
  data() {
    return {
      /**
       * Sest the active view
       *
       * @type  {string}
       */
      activeView: 'thumbnail',

      /**
       * Toggles the assets manager delete confirmation buttons
       *
       * @type  {boolean}
       */
      assetsDeleteConfirmationVisible: false,

      /**
       * Defines the sorting options values
       *
       * @type  {Array}
       */
      sortingOptions: [
        {
          title: 'File Name &uarr;',
          value: 'file_name|asc',
        },
        {
          title: 'File Name &darr;',
          value: 'file_name|desc',
        },
        {
          title: 'Modification Date &uarr;',
          value: 'updated_at|asc',
        },
        {
          title: 'Modification Date &darr;',
          value: 'updated_at|desc',
          selected: true,
        },
      ],

      /**
       * Containg the data passed to Select2 field-type module
       *
       * @type  {Object}
       */
      imageSizesOptions: {
        disabled: false,
        id: 0,
        lazyLoading: false,
        multiple: false,
        name: 'imageSizesPicker',
        placeholder: 'Select image size',
        preloadDataConfig: {
          method: 'post',
          payload: {
            include_relations: false,
          },
          resultsObjectPath: 'body.body.entries',
          valuesOfInterest: {
            id: 'system.id',
            text: 'system.anchor_text',
          },
          url: false,
        },
        readonly: false,
        relatedTableName: 'image_sizes',
        required: true,
        value: null,
      },

      /**
       * Contains all image sizes, as returned by the API
       *
       * @type  {Array}
       */
      imageSizes: [],

      /**
       * If an image size is selected, this proprty will containg the selected image size object
       *
       * @type  {mixed}
       */
      selectedImageSize: null,

      /**
       * If an image crop area is edited this property will be an object containing values needed to perform
       * the imageUpdate action
       *
       * @type  {object}
       */
      editedResizeImageData: null,

      /**
       * If focal point has been edited this property will be an object containing values needed to perform
       * the imageUpdate action
       *
       * @type  {object}
       */
      editedFocalPointData: null,

      /**
       * A property watched by ImgAreaSelect module used to reinstantiate the plugin as needed
       *
       * @type  {boolean}
       */
      imgAreaSelectActiveRefresh: false,

      /**
       * Used as prop to instruct the AssetEditor to engage a getFields() action
       *
       * @type  {string}
       */
      updateAssetEditor: 1,
    }
  },

  /**
   * Set the components
   *
   * @return  {object}
   */
  components: {
    AssetAdvancedSearch,
    AssetEditor,
    ImageSizes,
    InfiniteLoading,
    Layout,
  },

  /**
   * Set the methods
   *
   * @type  {object}
   */
  methods: {
    ...mapActions('admin', ['setAssetManagerSearchFilterObject']),

    getObjectPropertyValueByPath,

    /**
     * Shows deletion confirmation dialogue
     *
     * @param   {boolean}  value
     * @return  {void}
     */
    deleteAsset(id) {
      store.dispatch(`${this.assetsManager.moduleName}/deleteAsset`, { id })

      this.assetsDeleteConfirmationVisible = false
    },

    /**
     * Fired on detected onChange event
     *
     * @param   {integer}  options.value
     * @return  {void}
     */
    updateSelectedImageSize({ value }) {
      this.selectedImageSize = _.find(this.imageSizes, imageSize => {
        return _.get(imageSize, 'system.id') === parseInt(value)
      })
    },

    /**
     * Fired upon receiving the onImgAreaSelect event from the ImgAreaSelect module
     *
     * @param   {integer}  options.id
     * @param   {integer}  options.X
     * @param   {integer}  options.Y
     * @param   {integer}  options.width
     * @return  {void}
     */
    onFocalPointSelect({ x, y }) {
      this.editedFocalPointData = {
        id: _.get(this.assetsManager.editedEntry, 'system.id'),
        x,
        y,
      }
    },

    /**
     * Fired upon receiving the onImgAreaSelect event from the ImgAreaSelect module
     *
     * @param   {integer}  options.id
     * @param   {integer}  options.height
     * @param   {integer}  options.topX
     * @param   {integer}  options.topY
     * @param   {integer}  options.width
     * @return  {void}
     */
    onImgAreaSelect({ id, height, topX, topY, width }) {
      this.editedResizeImageData = {
        height,
        id,
        width,
        x: topX,
        y: topY,
      }
    },

    /**
     * Instructs the AssetEditor to engage a getFields() action
     *
     * @return  {void}
     */
    setUpdateAssetEditor() {
      this.updateAssetEditor = moment().valueOf()
    },

    /**
     * Shows deletion confirmation dialogue
     *
     * @param   {boolean}  value
     * @return  {void}
     */
    predeleteAsset(value) {
      this.assetsDeleteConfirmationVisible = value
    },

    /**
     * Dispatches the resizeImage action after clicking the 'Update Resized Image' button
     *
     * @return  {void}
     */
    resizeImage() {
      if (!this.editedResizeImageData) {
        return
      }

      store
        .dispatch(
          `${this.assetsManager.moduleName}/resizeImage`,
          this.editedResizeImageData
        )
        .then(() => {
          this.editedResizeImageData = null
        })
        .catch(response => {
          pError('Failed to complete the resize image action.', response)
        })
    },

    /**
     * Dispatches the editedFocalPointData action after clicking the 'Update Resized Image' button
     *
     * @return  {void}
     */
    resizeImagesUsingFocalPoint() {
      if (!this.editedFocalPointData) {
        return
      }

      store
        .dispatch(
          `${this.assetsManager.moduleName}/resizeImagesUsingFocalPoint`,
          this.editedFocalPointData
        )
        .then(() => {
          this.editedFocalPointData = null

          this.toggleAdjustFocalPoint()
        })
        .catch(response => {
          pError('Failed to complete the resize images action.', response)
        })
    },

    /**
     * Toggles the imgAreaSelectActiveRefresh property in order to reinstantiate the ImgAreaSelect plugin
     *
     * @return  {void}
     */
    revertSelection() {
      this.editedResizeImageData = null

      this.imgAreaSelectActiveRefresh = !this.imgAreaSelectActiveRefresh
    },

    /**
     * Toggles the imgAreaSelectActiveRefresh property in order to reinstantiate the ImgAreaSelect plugin
     *
     * @return  {void}
     */
    revertFocalPointSelection() {
      this.editedFocalPointData = null
    },

    /**
     * Sets the search parameter
     *
     * @param  {event}  event
     * @return  {void}
     */
    search: _.debounce(function(event) {
      const payload = {
        fields: {
          file_name: {
            like: event.target.value,
            or: 1,
          },
          title: {
            like: event.target.value,
            or: 1,
          },
          alt_text: {
            like: event.target.value,
            or: 1,
          },
          // tags: {
          //     title: {
          //         like: event.target.value,
          //     },
          //     or: 1,
          // },
        },
      }

      store
        .dispatch(`${this.assetsManager.moduleName}/setFilterObject`, {
          value: payload,
        })
        .then(() => {
          store
            .dispatch(`${this.assetsManager.moduleName}/setFilterObject`, {
              value: payload,
            })
            .then(() => {
              // Remember the search filter in admin module as well
              this.setAssetManagerSearchFilterObject({ value: payload }).then(
                () => {
                  // this.$refs.infiniteLoading.$emit('$InfiniteLoading:reset');
                  this.$refs.infiniteLoading.stateChanger.reset()
                }
              )
            })
        })
    }, 500),

    /**
     * Adds the asset to a list of selected assets
     *
     * @param   {object}  asset
     * @return  {void}
     */
    selectAsset(asset) {
      this.setUpdateAssetEditor()

      if (!this.assetsManager.selectionDisabled) {
        store.dispatch(`${this.assetsManager.moduleName}/selectAsset`, {
          asset,
        })
      }

      store.dispatch(`${this.assetsManager.moduleName}/selectActiveAsset`, {
        asset: _.cloneDeep(asset),
      })
    },

    /**
     * Removes the asset from a list of selected assets
     *
     * @param   {object}  asset
     * @return  {void}
     */
    deselectAsset(asset) {
      store.dispatch(`${this.assetsManager.moduleName}/deselectAsset`, {
        asset,
      })
    },

    /**
     * Sets the active view
     *
     * @param   {string}  view
     * @return  {void}
     */
    setActiveView(view) {
      this.activeView = view
    },

    /**
     * Sets the sorting option
     *
     * @param  {event}  event
     * @return  {void}
     */
    setSortingOption(event) {
      store.dispatch(`${this.assetsManager.moduleName}/setSortingOption`, {
        value: event.target.value,
      })

      // this.$refs.infiniteLoading.$emit('$InfiniteLoading:reset');
      this.$refs.infiniteLoading.stateChanger.reset()
    },

    /**
     * Submits the search, triggered by the advanced search emit
     *
     * @return  {void}
     */
    submitSearch() {
      // this.$refs.infiniteLoading.$emit('$InfiniteLoading:reset');
      this.$refs.infiniteLoading.stateChanger.reset()
    },

    /**
     * Emits the event upon hitting bottom of an assets container
     *
     * @return  {void}
     */
    onInfinite() {
      store
        .dispatch(`${this.assetsManager.moduleName}/loadPaginatedAssets`)
        .then(
          () => {
            // this.$refs.infiniteLoading.$emit('$InfiniteLoading:loaded');
            this.$refs.infiniteLoading.stateChanger.loaded()
          },
          () => {
            // this.$refs.infiniteLoading.$emit('$InfiniteLoading:complete');
            this.$refs.infiniteLoading.stateChanger.complete()
          }
        )
    },

    /**
     * Toggles the advanced search mode
     *
     * @return  {void}
     */
    toggleAdvancedSearch() {
      store.dispatch(`${this.registeredModuleName}/toggleAdvancedSearch`)
    },

    /**
     * Toggles the adjust focal point visibility
     *
     * @return  {void}
     */
    toggleAdjustFocalPoint() {
      store.dispatch(`${this.registeredModuleName}/toggleAdjustFocalPoint`)

      this.editedFocalPointData = null
    },
  },

  /**
   * Set the computed properties
   *
   * @type  {object}
   */
  computed: {
    ...mapGetters({
      admin: 'admin/admin',
      notification: 'notification/notification',
      ui: 'ui/ui',
    }),

    /**
     * Define the assetsManager getter
     *
     * @return  {object}
     */
    assetsManager() {
      const getterName = `${this.registeredModuleName}/${this.registeredModuleName}`

      return this.$store.getters[getterName]
    },

    /**
     * Determines the image resizer sidebar panel visibility
     *
     * @return  {Boolean}
     */
    isImageResizerSidebarPanelVisible() {
      return !this.assetsManager.advancedSearchActive && this.isAssetSelected
    },

    /**
     * Sets the sidebar visibility
     *
     * @return boolean
     */
    isSidebarVisible() {
      if (this.assetsManager.uploadModeActive) {
        return false
      }

      return (
        this.isAdvancedSearchSidebarPanelVisible ||
        this.isFocalPointPickerSidebarPanelVisible ||
        this.isFileDetailsSidebarPanelVisible
      )
    },

    /**
     * Determines the advanced search sidebar panel visibility
     *
     * @return  {Boolean}
     */
    isAdvancedSearchSidebarPanelVisible() {
      return this.assetsManager.advancedSearchActive
    },

    /**
     * Determines the status of the infinite loading mechanism
     *
     * @return  {Boolean}
     */
    isInfiniteLoadingActive() {
      if (this.assetsManager.uploadModeActive) {
        return false
      }

      return !this.assetsManager.adjustFocalPointActive
    },

    /**
     * Determines the file details sidebar panel visibility
     *
     * @return  {Boolean}
     */
    isFileDetailsSidebarPanelVisible() {
      return (
        !this.assetsManager.advancedSearchActive &&
        this.isAssetSelected &&
        !this.assetsManager.adjustFocalPointActive
      )
    },

    /**
     * Determines the focal point picker sidebar visibility
     *
     * @return  {Boolean}
     */
    isFocalPointPickerSidebarPanelVisible() {
      return (
        !this.assetsManager.advancedSearchActive &&
        this.isAssetSelected &&
        this.assetsManager.adjustFocalPointActive
      )
    },

    /**
     * Determines the main focal point picker component visibility
     *
     * @return  {Boolean}
     */
    isMainFocalPointPickerComponentVisible() {
      return this.isFocalPointPickerSidebarPanelVisible
    },

    /**
     * Determines the main file listing component visibility
     *
     * @return  {Boolean}
     */
    isMainFileListingVisible() {
      return (
        !this.isMainFocalPointPickerComponentVisible &&
        !this.assetsManager.uploadModeActive
      )
    },

    /**
     * Returns true if there's an asset selected
     *
     * @return  {Boolean}
     */
    isAssetSelected() {
      return _.has(this.assetsManager.editedEntry, 'system.id')
    },

    /**
     * Calculates if the table class should be activated or not
     *
     * @return  {object}
     */
    isTableClassActive() {
      return {
        active: this.activeView === 'table',
      }
    },

    /**
     * Calculates if the thumbnail class should be activated or not
     *
     * @return  {object}
     */
    isThumbnailClassActive() {
      return {
        active: this.activeView === 'thumbnail',
      }
    },

    /**
     * Returns the resized image object
     *
     * @return  {object}
     */
    resizedImage() {
      const resizedImages = _.get(
        this.assetsManager.editedEntry,
        'fields.resized_images'
      )

      _.forEach(resizedImages, function(value, key) {
        resizedImages[key].fields.image_size_id =
          value.fields.image_size.system.id
      })

      return _.find(resizedImages, resizedImage => {
        const test =
          _.get(resizedImage, 'fields.image_size_id') ===
          parseInt(this.selectedImageSize.system.id)
        return (
          _.get(resizedImage, 'fields.image_size_id') ===
          parseInt(this.selectedImageSize.system.id)
        )
      })
    },

    /**
     * Returns the resized image URL
     *
     * @return  {string}
     */
    resizedImageURL() {
      if (
        _.has(this.resizedImage, 'fields.file_url') &&
        _.has(this.resizedImage, 'system.updated_at')
      ) {
        store.dispatch(`${this.assetsManager.moduleName}/setFileUrl`, {
          value: this.resizedImage.fields.file_url,
          image_size: this.selectedImageSize.system.id,
        })

        return this.resizedImage.fields.file_url
      }

      return null
    },

    /**
     * Gets the value
     *
     * @return  {string}
     */
    getSearchByFilenameValue() {
      const path = 'filter.fields.file_name.like'

      return _.get(this.assetsManager, path, null)
    },
  },

  /**
   * Set the mounted hook
   *
   * @return  {void}
   */
  mounted() {
    this.$nextTick(function() {
      // Load the shared per-photon-module-based search filter
      store.dispatch(`${this.assetsManager.moduleName}/setFilterObject`, {
        value: this.admin.assetManagerSearchFilter,
      })

      // Load the shared per-photon-module-based search filter to advancedSearch model as well,
      // so that advanced serch fields are populated with correct search parameters
      store.dispatch('assetsAdvancedSearch/setAdvancedSearchFilterObject', {
        value: this.admin.assetManagerSearchFilter,
      })

      this.imageSizesOptions.disabled = !this.isAssetSelected
    })
  },

  watch: {
    'assetsManager.editedEntry': {
      deep: true,
      handler() {
        this.imageSizesOptions.disabled = !this.isAssetSelected
      },
    },

    isImageResizerSidebarPanelVisible(newEntry, oldEntry) {
      if (newEntry !== oldEntry) {
        this.updateSelectedImageSize({ value: 1 })
      }
    },
  },
}
